import { useTranslation } from 'react-i18next';

import SplitValueElement from '@/components/atoms/SplitValueElement';
import Tooltip from '@/components/atoms/Tooltip';
import { formatNumberByLocale, parseLocale } from '@/utils/localeFormatters';

import styles from './LiceReportLocationTable.module.scss';

/**
 * A lice stage as "average | total", which the report shows for each of its three stages.
 *
 * Two components rather than one configurable: the body cell and the footer cell differ in
 * markup (`<span>` against `<em>`), in emphasis, and in whether each half carries a tooltip.
 * Folding them into one with four optional props would make every call site read as a
 * configuration rather than as a cell.
 */

interface CellProps {
    average: number | undefined;
    total: number | undefined;
}

/** One pen's row. The averages carry two decimals; a count of lice does not. */
export function AvgTotalCell({ average, total }: CellProps) {
    const { i18n } = useTranslation();
    const locale = parseLocale(i18n.language);

    return (
        <SplitValueElement>
            <span>{formatNumberByLocale(average, locale, 2)}</span>
            <span>{formatNumberByLocale(total, locale, 0)}</span>
        </SplitValueElement>
    );
}

interface SummaryCellProps extends CellProps {
    averageTooltip: string;
    totalTooltip: string;
}

/** The location's own row, at the foot of the table. */
export function AvgTotalSummaryCell({
    average,
    total,
    averageTooltip,
    totalTooltip,
}: SummaryCellProps) {
    const { i18n } = useTranslation();
    const locale = parseLocale(i18n.language);

    return (
        // `fitContent` matters: the tooltip's wrapper is `width: 100%` by default, which would
        // stretch inside the split layout and aim the bubble at the middle of the cell rather
        // than at the figure.
        <SplitValueElement variant="highlight">
            <Tooltip content={averageTooltip} layout="fit" targetClassName={styles.footerTooltip}>
                <em>{formatNumberByLocale(average, locale, 2)}</em>
            </Tooltip>
            <Tooltip content={totalTooltip} layout="fit" targetClassName={styles.footerTooltip}>
                <em>{formatNumberByLocale(total, locale, 0)}</em>
            </Tooltip>
        </SplitValueElement>
    );
}
